import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, Shield, ChevronDown, Sun, Moon, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLang } from '../context/LangContext';

const baseLinks = [
  { to: '/ask', key: 'nav.ask' },
  { to: '/guidance', key: 'nav.guidance' },
  { to: '/patterns', key: 'nav.patterns' },
  { to: '/practice', key: 'nav.practice' },
  { to: '/book', key: 'nav.book' },
  { to: '/pricing', key: 'nav.pricing' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLang();
  const dropdownRef = useRef(null);

  const navLinks = user?.isAdmin
    ? [...baseLinks, { to: '/admin', key: 'nav.admin', icon: Shield }]
    : baseLinks;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close dropdown on navigation
  useEffect(() => {
    setDropdownOpen(false);
    setOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(9,11,11,0.92)', borderBottom: '1px solid rgba(42,37,32,0.4)' }}>
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-warm font-serif text-lg tracking-wide hover:text-gold transition-colors">
          One Light, Many Lamps
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm tracking-wide transition-colors flex items-center gap-1 ${
                location.pathname === link.to
                  ? 'text-gold'
                  : 'text-warm-100/70 hover:text-warm'
              }`}
            >
              {link.icon && <link.icon size={13} />}
              {t(link.key)}
            </Link>
          ))}

          {/* Auth section */}
          {!loading && (
            user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(p => !p)}
                  className={`flex items-center gap-1.5 text-sm tracking-wide transition-colors ${
                    dropdownOpen ? 'text-gold' : 'text-warm-100/70 hover:text-warm'
                  }`}
                >
                  <User size={14} />
                  <span>{user.name || t('menu.account')}</span>
                  {user.tier === 'premium' && (
                    <span className="bg-gold/20 text-gold text-[10px] px-1.5 py-0.5 rounded-full">Premium</span>
                  )}
                  <ChevronDown size={12} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-dark-100 border border-dark-200/50 rounded-xl shadow-xl py-1 animate-fade-in">
                    <Link to="/account" className="flex items-center gap-2 px-4 py-2.5 text-sm text-warm-100/70 hover:text-warm hover:bg-dark-200/30 transition-colors">
                      {t('menu.account')}
                    </Link>
                    <Link to="/billing" className="flex items-center gap-2 px-4 py-2.5 text-sm text-warm-100/70 hover:text-warm hover:bg-dark-200/30 transition-colors">
                      {t('menu.billing')}
                    </Link>

                    <div className="border-t border-dark-200/30 my-1" />

                    {/* Theme toggle */}
                    <button
                      onClick={toggleTheme}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-warm-100/70 hover:text-warm hover:bg-dark-200/30 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        {theme === 'dark' ? <Moon size={13} /> : <Sun size={13} />}
                        {t('menu.theme')}
                      </span>
                      <span className="text-warm-100/40 text-xs">{theme === 'dark' ? t('menu.dark') : t('menu.light')}</span>
                    </button>

                    {/* Language toggle */}
                    <button
                      onClick={() => setLang(lang === 'en' ? 'cn' : 'en')}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-warm-100/70 hover:text-warm hover:bg-dark-200/30 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Globe size={13} />
                        {t('menu.language')}
                      </span>
                      <span className="text-warm-100/40 text-xs">{lang === 'en' ? 'EN' : '中文'}</span>
                    </button>

                    <div className="border-t border-dark-200/30 my-1" />

                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-warm-100/50 hover:text-red-400 hover:bg-dark-200/30 transition-colors"
                    >
                      {t('menu.signOut')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="text-sm tracking-wide text-gold/80 hover:text-gold transition-colors"
              >
                {t('nav.signIn')}
              </Link>
            )
          )}
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-warm/70 hover:text-warm"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-dark-100 border-b border-dark-200/50 animate-fade-in">
          <div className="max-w-5xl mx-auto px-6 py-4 flex flex-col gap-4">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`text-sm tracking-wide transition-colors ${
                  location.pathname === link.to
                    ? 'text-gold'
                    : 'text-warm-100/70 hover:text-warm'
                }`}
              >
                {t(link.key)}
              </Link>
            ))}

            {/* Mobile theme/lang */}
            <div className="flex gap-3 pt-2 border-t border-dark-200/30">
              <button onClick={toggleTheme} className="flex items-center gap-1.5 text-warm-100/50 text-xs hover:text-warm">
                {theme === 'dark' ? <Moon size={12} /> : <Sun size={12} />}
                {theme === 'dark' ? t('menu.dark') : t('menu.light')}
              </button>
              <button onClick={() => setLang(lang === 'en' ? 'cn' : 'en')} className="flex items-center gap-1.5 text-warm-100/50 text-xs hover:text-warm">
                <Globe size={12} />
                {lang === 'en' ? 'EN' : '中文'}
              </button>
            </div>

            {!loading && (
              user ? (
                <>
                  <Link to="/account" onClick={() => setOpen(false)} className="text-sm text-warm-100/70 hover:text-warm">
                    {t('menu.account')}
                  </Link>
                  <Link to="/billing" onClick={() => setOpen(false)} className="text-sm text-warm-100/70 hover:text-warm">
                    {t('menu.billing')}
                  </Link>
                  <button onClick={() => { handleLogout(); setOpen(false); }} className="text-left text-sm text-warm-100/50 hover:text-red-400">
                    {t('menu.signOut')}
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-gold/80 hover:text-gold">
                  {t('nav.signIn')}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
